import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { MediaFrame, type Frame, type Media } from './Media';
import { RoomSequence, type RoomFrame } from './RoomSequence';

/* ==========================================================================
   SHOW SPINE: one show told by the clock, from the first message to settling
   up, for whichever side the visitor picks (a venue or an artist). The two
   tracks share every component and differ only in the words and the screens
   (src/content/showSpine.ts).

   Desktop: the stops scroll on the left and the screen for the stop in the
   middle of the viewport sits in a sticky panel on the right. Phone: each
   stop carries its own screen inline, because a sticky panel beside text has
   no room on a phone. The choice lives in the URL (?as=artist), so a page
   can link straight to a track.
   ========================================================================== */

export type Visual =
  | { kind: 'media'; media: Media; frame: Frame; caption?: string }
  | { kind: 'room'; frames: RoomFrame[]; steps: string[] };

export interface Stop {
  /** When this happens, on the show's own clock ("Two weeks out", "8:04 PM"). */
  when: string;
  title: string;
  body: string;
  visual: Visual;
}

export interface Track {
  id: 'venue' | 'artist';
  /** The chooser's label: "I book for a venue". */
  choice: string;
  stops: Stop[];
}

const VisualView: React.FC<{ visual: Visual }> = ({ visual }) => {
  if (visual.kind === 'room') return <RoomSequence frames={visual.frames} steps={visual.steps} />;
  return <MediaFrame media={visual.media} frame={visual.frame} caption={visual.caption} />;
};

/** Which stop is at the middle of the viewport. */
function useActiveStop(count: number, resetKey: string) {
  const refs = useRef<(HTMLLIElement | null)[]>([]);
  const [active, setActive] = useState(0);

  // The active stop is the last one whose top has passed the middle of the
  // viewport. Measured on scroll, once per frame: six rects is nothing, and
  // unlike an IntersectionObserver it can't skip a stop on a fast jump (an
  // anchor link, a keyboard End) or lag in a throttled tab.
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const middle = window.innerHeight / 2;
      let next = 0;
      refs.current.slice(0, count).forEach((el, i) => {
        if (el && el.getBoundingClientRect().top <= middle) next = i;
      });
      setActive(next);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [count, resetKey]);

  return { refs, active };
}

export const ShowSpine: React.FC<{ tracks: Track[]; heading: string }> = ({ tracks, heading }) => {
  const [params, setParams] = useSearchParams();
  const track = tracks.find((t) => t.id === params.get('as')) ?? tracks[0];
  const { refs, active } = useActiveStop(track.stops.length, track.id);

  const choose = (id: Track['id']) => {
    const next = new URLSearchParams(params);
    if (id === tracks[0].id) next.delete('as');
    else next.set('as', id);
    // Only the search changes, so the layout's scroll-to-top (keyed on the
    // path and hash) leaves the visitor where they are.
    setParams(next, { replace: true });
  };

  return (
    <section id="how-it-works" className="border-t border-grey-200 scroll-mt-14">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 section">
        <h2 className="font-display text-3xl sm:text-[2.5rem] text-ink leading-[1.05] tracking-tight max-w-[22ch] text-balance">
          {heading}
        </h2>

        {/* The choice. Two plain buttons, one pressed. */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl" role="group" aria-label="Follow the show as">
          {tracks.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-pressed={t.id === track.id}
              onClick={() => choose(t.id)}
              className={cn(
                'rounded-md border px-5 py-4 text-left transition-colors',
                t.id === track.id
                  ? 'border-ink bg-ink text-grey-50'
                  : 'border-grey-300 bg-grey-50 text-ink hover:border-grey-500',
              )}
            >
              <span className="label-stamp block opacity-70">Follow the show as</span>
              <span className="mt-1 block font-display text-xl tracking-tight">{t.choice}</span>
            </button>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 lg:gap-x-12">
          {/* The stops, on a clock rail */}
          <ol className="lg:col-span-5 relative border-l border-grey-300 pl-6 sm:pl-8">
            {track.stops.map((stop, i) => (
              <li
                key={`${track.id}-${stop.when}`}
                ref={(el) => { refs.current[i] = el; }}
                data-index={i}
                className="relative pb-16 lg:min-h-[52vh] lg:pb-0"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute -left-[calc(1.5rem+5px)] sm:-left-[calc(2rem+5px)] top-1.5 h-[9px] w-[9px] rounded-full border-2 transition-colors motion-reduce:transition-none',
                    i === active ? 'border-primary-600 bg-primary-600' : 'border-grey-400 bg-background',
                  )}
                />
                <p className="label-stamp text-grey-600">{stop.when}</p>
                <h3 className="mt-2 font-display text-2xl sm:text-3xl text-ink leading-tight tracking-tight">
                  {stop.title}
                </h3>
                <p className="mt-3 text-base text-grey-800 leading-relaxed max-w-[46ch]">{stop.body}</p>
                {/* On a phone the screen goes with its stop. */}
                <div className="mt-6 lg:hidden">
                  <VisualView visual={stop.visual} />
                </div>
              </li>
            ))}
          </ol>

          {/* Desktop: the screen for the stop in view, held in place. */}
          <div className="hidden lg:block lg:col-span-7">
            <div className="sticky top-24">
              <div className="relative">
                {track.stops.map((stop, i) => (
                  <div
                    key={`${track.id}-${stop.when}`}
                    aria-hidden={i !== active}
                    className={cn(
                      'transition-opacity duration-300 motion-reduce:transition-none',
                      i === active ? 'relative opacity-100' : 'pointer-events-none absolute inset-x-0 top-0 opacity-0',
                    )}
                    // Keep inactive stops out of the tab order (the room demo has controls).
                    {...(i !== active ? { inert: '' } : {})}
                  >
                    <VisualView visual={stop.visual} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
